import React, { useState, useRef, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from "recharts";
import Spinner from "./components/Spinner/Spinner";

// Helper hook: scroll to bottom on new messages
function useScrollToBottom(dependencies) {
  const chatEndRef = useRef(null);
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, dependencies);
  return chatEndRef;
}

// ─────────────── Meal Card (in the Meal Plan) ───────────────
// const MealCard = ({ meal, mealType, isSelected, onSelect }) => {
//   return (
//     <div style={{ ...styles.mealCard, position: "relative" }}>
//       {/* Checkbox in the top-right */}
//       <input
//         type="checkbox"
//         checked={isSelected}
//         onChange={() => onSelect(meal.meal_id)}
//         style={styles.checkbox}
//       />
//       <h4>{mealType}</h4>
//       <p>
//         <strong>{meal.meal}</strong>
//       </p>
//       {meal.ingredients && meal.ingredients.length > 0 && (
//         <div>
//           <p>Ingredients:</p>
//           <ul>
//             {meal.ingredients.map((ing, i) => (
//               <li key={i}>{ing}</li>
//             ))}
//           </ul>
//         </div>
//       )}
//       {meal.calories && <p>Calories: {meal.calories}</p>}
//     </div>
//   );
// };
const MealCard = ({ meal, mealType, isSelected, onSelect }) => {
  // Define bubble styling based on the source type.
  const sourceBubbleStyle = {
    backgroundColor:
      meal.source === "packaged"
        ? "blue"
        : meal.source === "restaurant"
        ? "red"
        : "transparent",
    color: "white",
    borderRadius: "10px",
    padding: "2px 6px",
    marginLeft: "8px",
  };

  return (
    <div style={{ ...styles.mealCard, position: "relative" }}>
      {/* Checkbox in the top-right */}
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onSelect(meal.meal_id)}
        style={styles.checkbox}
      />
      <h4>{mealType}</h4>
      <p>
        <strong>{meal.mealName}</strong>
        {(meal.source === "packaged" || meal.source === "restaurant") &&
          meal.sourceDetails && (
            <span style={sourceBubbleStyle}>
              {meal.sourceDetails.name || ""}
            </span>
          )}
      </p>
      {meal.recipes && meal.recipes.length > 0 && (
        <div>
          {/* <p>Recipes:</p> */}
          <ul>
            {meal.recipes.map((recipe, i) => (
              <li key={i}>
                {recipe.recipeDetails && recipe.recipeDetails.name ? (
                  <>
                    {recipe.recipeDetails.name}
                    {recipe.nutritionPerServing && (
                      <span>
                        {" "}
                        - {recipe.nutritionPerServing.calories} Calories
                      </span>
                    )}
                  </>
                ) : (
                  "Recipe"
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {meal.totalMacros && (
        <div>
          <p>Total Macros:</p>
          <ul>
            <li>Calories: {meal.totalMacros.calories}</li>
            <li>Protein: {meal.totalMacros.protein}</li>
            <li>Fats: {meal.totalMacros.fat}</li>
            <li>Carbs: {meal.totalMacros.carbs}</li>
          </ul>
        </div>
      )}
    </div>
  );
};

// ─────────────── Meal List Card (in the Meal List Column) ───────────────
const MealListCard = ({ meal, isSelected, onToggleSelect }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  return (
    <div style={styles.mealListCard}>
      {/* Checkbox in the top-left */}
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggleSelect(meal.meal_id)}
        style={styles.listCheckbox}
      />

      {/* Meal Image & Name Container */}
      <div style={styles.mealHeader}>
        <img
          src={meal.imageURL}
          alt={meal.name}
          style={styles.mealImage}
          onClick={() => setModalOpen(true)}
        />
        <div style={styles.mealNameContainer}>
          <h4 style={styles.mealName}>{meal.name}</h4>
        </div>
      </div>

      {/* Nutritional Info (Two-Column Layout) */}
      <div style={styles.macroContainer}>
        <div style={styles.macroColumn}>
          <p>
            <strong>Calories:</strong> {meal.calories}
          </p>
          <p>
            <strong>Protein:</strong> {meal.protein}g
          </p>
        </div>
        <div style={styles.macroColumn}>
          <p>
            <strong>Fat:</strong> {meal.fat}g
          </p>
          <p>
            <strong>Carbs:</strong> {meal.carbs}g
          </p>
        </div>
      </div>

      {/* Meal Type & Time (Two-Column Layout) */}
      <div style={styles.macroContainer}>
        <div style={styles.macroColumn}>
          <p>
            <strong>Type:</strong> {meal.mealType}
          </p>
        </div>
        <div style={styles.macroColumn}>
          <p>
            <strong>Time:</strong> {meal.time}
          </p>
        </div>
      </div>
      {/* Pop-up Modal for Full-Size Image */}
      {isModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setModalOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              style={styles.closeButton}
              onClick={() => setModalOpen(false)}
            >
              ✖
            </button>
            <img
              src={meal.imageURL}
              alt={meal.name}
              style={styles.modalImage}
            />
          </div>
        </div>
      )}
    </div>
  );
};
// <input
//   type="checkbox"
//   checked={isSelected}
//   onChange={() => onToggleSelect(meal.meal_id)}
//   style={styles.listCheckbox}
// />
// Some default meals for the meal list demo
const defaultMeals = [
  {
    meal_id: "meal1",
    name: "Chicken Salad",
    calories: 350,
    protein: 30,
    fat: 15,
    carbs: 20,
  },
  {
    meal_id: "meal2",
    name: "Veggie Wrap",
    calories: 250,
    protein: 10,
    fat: 8,
    carbs: 35,
  },
  {
    meal_id: "meal3",
    name: "Beef Stir Fry",
    calories: 500,
    protein: 40,
    fat: 20,
    carbs: 45,
  },
  {
    meal_id: "meal4",
    name: "Quinoa Bowl",
    calories: 400,
    protein: 15,
    fat: 10,
    carbs: 60,
  },
];

// ─────────────── User Info Panel ───────────────
const UserInfo = ({ memberId }) => {
  // const [memberId, setMemberId] = useState("6571e3cb9a25c707c1ab32b7");
  // Sample user data
  const [user, setUser] = useState({
    firstName: "Uknown",
    lastName: "Member",
    profilePicUrl: "", // Replace with a URL if available
  });

  // Sample progress percentages (for the past two weeks)
  const progressData = {
    calorie: 75,
    protein: 60,
    fat: 80,
    carb: 50,
  };

  // Generate sample data for each hour of the day (0-23)
  const [glucoseData, setGlucoseData] = useState(
    Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}:00`,
      avgGlucose: 90 + (i % 3) * 10,
    }))
  );

  const [mealTimes, setMealTimes] = useState([
    { hour: 8, label: "Breakfast" },
    { hour: 12, label: "Lunch" },
    { hour: 18, label: "Dinner" },
    { hour: 21, label: "Snack" },
  ]);

  const [macroData, setMacroData] = useState(
    Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}:00`,
      protein: 20 + (i % 4) * 5,
      fat: 10 + (i % 3) * 3,
      carbs: 30 + (i % 5) * 4,
    }))
  );

  // Data for the glycemic breakdown pie chart
  const [glycemicData, setGlycemicData] = useState([
    { name: "Low", value: 40 },
    { name: "High", value: 30 },
    { name: "Neutral", value: 30 },
  ]);
  const pieColors = ["#82ca9d", "#8884d8", "#ffc658"];

  // Sample high glycemic foods list
  const [highGlycemicFoods, setHighGlycemicFoods] = useState([
    { id: 1, name: "White Bread", calories: 200 },
    { id: 2, name: "Sugary Cereal", calories: 150 },
    { id: 3, name: "Candy Bar", calories: 250 },
    { id: 4, name: "Soda", calories: 180 },
  ]);

  const getMemberData = async () => {
    console.log(`fetching data for member ${memberId}`);
    fetch(`http://localhost:8080/mealplan/memberInfo/${memberId}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((userData) => {
        console.log(userData);
        setUser(userData.personalDetails);
        setGlucoseData(userData.hourlyGlucose);
        setGlycemicData(userData.glycemicBreakdown);
        setHighGlycemicFoods(userData.highGlycemicFoods);
        setMealTimes(userData.estimatedMealTimes);
        setMacroData(userData.cumulativeMacros);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    getMemberData();
  }, []);

  return (
    <div style={styles.userInfoContainer}>
      {/* User header */}
      <div style={styles.userProfile}>
        <div>
          <h3>
            {user.firstName} {user.lastName}
          </h3>
          {/* <p style={styles.userBio}>
            This is a short bio line. You can have up to two lines here.
          </p> */}
        </div>
        <div style={styles.profilePicPlaceholder}>
          <img
            src={user.profilePicUrl}
            alt="Profile"
            style={{ width: "100%", height: "100%", borderRadius: "50%" }}
          />
        </div>
      </div>
      {/* Progress Section (using simple divs as circular progress placeholders) */}
      <h4>Percentage of Days Within Range (Past Week)</h4>
      <div style={styles.progressSection}>
        {["calorie", "protein", "fat", "carb"].map((key) => (
          <div key={key} style={styles.circularProgress}>
            <div style={styles.circularProgressBar}>{progressData[key]}%</div>
            <p style={styles.progressLabel}>{key}</p>
          </div>
        ))}
      </div>
      {/* Glucose Graph */}
      <div style={styles.graphSection}>
        <h4>Hourly Glucose (Past Week)</h4>
        <LineChart width={300} height={200} data={glucoseData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" hide={true} />
          <YAxis />
          <Tooltip />
          <Legend />

          {/* Glucose Line */}
          <Line
            type="monotone"
            dataKey="avgGlucose"
            stroke="#8884d8"
            name="Avg Glucose"
          />

          {/* Meal Time Markers */}
          {mealTimes.map((meal, index) => (
            <ReferenceLine
              key={index}
              x={`${meal.hour}:00`}
              stroke="red"
              strokeDasharray="3 3"
              label={{
                value: meal.label,
                position: "top",
                fill: "red",
                fontSize: 12,
              }}
            />
          ))}

          {/* Hidden Line for Meal Time Legend */}
          <Line
            type="monotone"
            dataKey="fake"
            stroke="red"
            name="Meal Time"
            dot={false}
            activeDot={false}
          />
        </LineChart>
      </div>
      {/* Macro Consumption Graph */}
      <div style={styles.graphSection}>
        <h4>Hourly Macro Consumption (Past Week)</h4>
        <LineChart width={250} height={150} data={macroData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" hide={true} />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="carbs" stroke="#8884d8" name="Carbs" />
          <Line
            type="monotone"
            dataKey="protein"
            stroke="#82ca9d"
            name="Protein"
          />
          <Line type="monotone" dataKey="fat" stroke="#ff7300" name="Fat" />
        </LineChart>
      </div>
      {/* Glycemic Breakdown */}
      <div style={styles.glycemicSection}>
        <h4>Glycemic Breakdown (Past Week)</h4>
        <div style={styles.pieAndListContainer}>
          <PieChart width={200} height={200}>
            <Pie
              data={glycemicData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={50}
              fill="#8884d8"
              label
            >
              {glycemicData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={pieColors[index % pieColors.length]}
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
          <div style={styles.highGlycemicList}>
            <h5>High Glycemic Foods</h5>
            {highGlycemicFoods.map((food) => (
              <div key={food.id} style={styles.glycemicFoodItem}>
                <span>{food.name}</span>
                {/* <span>{food.calories} cal</span> */}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────── Chat Popup (appears above the FAB) ───────────────
const ChatPopup = ({
  isOpen,
  onClose,
  chatMessages,
  sendMessage,
  input,
  setInput,
  loading,
}) => {
  const chatEndRef = useScrollToBottom([chatMessages]);
  if (!isOpen) return null;
  return (
    <div style={styles.chatPopup}>
      <div style={styles.chatPopupHeader}>
        <h3 style={{ fontSize: "1rem" }}>Mealplan Copilot</h3>
        <button style={styles.closeButton} onClick={onClose}>
          X
        </button>
      </div>
      <div style={styles.chatPopupWindow}>
        {chatMessages.map((msg, index) => (
          <div
            key={index}
            style={msg.role === "user" ? styles.userMsg : styles.assistantMsg}
          >
            <strong>{msg.role === "user" ? "You" : "Copilot"}:</strong>{" "}
            {msg.content}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      <div style={styles.inputArea}>
        <input
          type="text"
          style={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") sendMessage();
          }}
          placeholder="Type your message..."
          disabled={loading} // Disable input while loading
        />

        {/* Show loading animation if loading is true, otherwise show the send button */}
        {loading ? (
          <div style={styles.loader}>
            <div style={styles.dot}></div>
            <div style={styles.dot}></div>
            <div style={styles.dot}></div>
          </div>
        ) : (
          <button style={styles.button} onClick={sendMessage}>
            Send
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────── Settings Dropdown ───────────────
const SettingsDropdown = ({
  isOpen,
  onClose,
  settings,
  setSettings,
  mealSettings,
  setMealSettings,
  addMealSetting,
  removeMealSetting,
}) => {
  if (!isOpen) return null;
  return (
    <div style={styles.settingsDropdown}>
      <button style={styles.closeButton} onClick={onClose}>
        X
      </button>
      <h3 style={{ marginBottom: "0.5rem" }}>Settings</h3>
      <div style={styles.settingsSection}>
        <label>
          Daily Calorie Goal:
          <input
            type="number"
            value={settings.calorieGoal}
            onChange={(e) =>
              setSettings({ ...settings, calorieGoal: e.target.value })
            }
          />
        </label>
        <label>
          Allergies:
          <input
            type="text"
            value={settings.allergies}
            onChange={(e) =>
              setSettings({ ...settings, allergies: e.target.value })
            }
          />
        </label>
        <label>
          Dietary Restrictions:
          <input
            type="text"
            value={settings.dietaryRestrictions}
            onChange={(e) =>
              setSettings({ ...settings, dietaryRestrictions: e.target.value })
            }
          />
        </label>
        <label>
          Liked Cuisines:
          <input
            type="text"
            value={settings.likedCuisines}
            onChange={(e) =>
              setSettings({ ...settings, likedCuisines: e.target.value })
            }
          />
        </label>
        <label>
          Disliked Cuisines:
          <input
            type="text"
            value={settings.dislikedCuisines}
            onChange={(e) =>
              setSettings({ ...settings, dislikedCuisines: e.target.value })
            }
          />
        </label>
        <label>
          Days:
          <input
            type="number"
            value={settings.days}
            onChange={(e) =>
              setSettings({
                ...settings,
                days: parseInt(e.target.value, 10) || 0,
              })
            }
          />
        </label>
      </div>
      <h4>Meals Per Day</h4>
      <div style={styles.mealSettingsContainer}>
        {mealSettings.map((meal, index) => (
          <div key={index} style={styles.mealSettingCard}>
            <input
              type="text"
              placeholder="Meal Name"
              value={meal.name}
              onChange={(e) => {
                const newMealSettings = [...mealSettings];
                newMealSettings[index].name = e.target.value;
                setMealSettings(newMealSettings);
              }}
            />
            <label>
              % Daily Calories:
              <input
                type="number"
                value={meal.caloriePercent}
                onChange={(e) => {
                  const newMealSettings = [...mealSettings];
                  newMealSettings[index].caloriePercent = e.target.value;
                  setMealSettings(newMealSettings);
                }}
              />
            </label>
            <label>
              Protein %:
              <input
                type="number"
                value={meal.proteinPercent}
                onChange={(e) => {
                  const newMealSettings = [...mealSettings];
                  newMealSettings[index].proteinPercent = e.target.value;
                  setMealSettings(newMealSettings);
                }}
              />
            </label>
            <label>
              Fat %:
              <input
                type="number"
                value={meal.fatPercent}
                onChange={(e) => {
                  const newMealSettings = [...mealSettings];
                  newMealSettings[index].fatPercent = e.target.value;
                  setMealSettings(newMealSettings);
                }}
              />
            </label>
            <label>
              Carb %:
              <input
                type="number"
                value={meal.carbPercent}
                onChange={(e) => {
                  const newMealSettings = [...mealSettings];
                  newMealSettings[index].carbPercent = e.target.value;
                  setMealSettings(newMealSettings);
                }}
              />
            </label>
            <label>
              Time:
              <input
                type="time"
                value={meal.time}
                onChange={(e) => {
                  const newMealSettings = [...mealSettings];
                  newMealSettings[index].time = e.target.value;
                  setMealSettings(newMealSettings);
                }}
              />
            </label>
            <label>
              Liked Ingredients:
              <input
                type="text"
                value={meal.likedIngredients}
                onChange={(e) => {
                  const newMealSettings = [...mealSettings];
                  newMealSettings[index].likedIngredients = e.target.value;
                  setMealSettings(newMealSettings);
                }}
              />
            </label>
            <label>
              Disliked Ingredients:
              <input
                type="text"
                value={meal.dislikedIngredients}
                onChange={(e) => {
                  const newMealSettings = [...mealSettings];
                  newMealSettings[index].dislikedIngredients = e.target.value;
                  setMealSettings(newMealSettings);
                }}
              />
            </label>
            <button onClick={() => removeMealSetting(index)}>
              Remove Meal
            </button>
          </div>
        ))}
        <button onClick={addMealSetting}>Add Meal</button>
      </div>
    </div>
  );
};

// ─────────────── Main App Component ───────────────
function MealPlannerCopilot() {
  // States for meal plan, chat, and meal list
  const [chatMessages, setChatMessages] = useState([]);
  const [mealPlan, setMealPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [input, setInput] = useState("");
  const [selectedMealId, setSelectedMealId] = useState(null);
  const [selectedMealIds, setSelectedMealIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Food Logs"); // Default tab

  const [foodLogs, setFoodLogs] = useState([
    {
      meal_id: "fl1",
      name: "Oatmeal",
      calories: 200,
      protein: 5,
      fat: 3,
      carbs: 40,
    },
    {
      meal_id: "fl2",
      name: "Grilled Chicken",
      calories: 300,
      protein: 40,
      fat: 10,
      carbs: 5,
    },
  ]);

  const [databaseMeals, setDatabaseMeals] = useState([
    {
      meal_id: "db1",
      name: "Sushi Roll",
      calories: 400,
      protein: 30,
      fat: 8,
      carbs: 50,
    },
    {
      meal_id: "db2",
      name: "Pasta Alfredo",
      calories: 600,
      protein: 20,
      fat: 35,
      carbs: 70,
    },
  ]);

  // Filter meals based on search query
  const filteredFoodLogs = foodLogs.filter((meal) =>
    meal.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredDatabaseMeals = databaseMeals.filter((meal) =>
    meal.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Settings & meal settings state
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    calorieGoal: 2000,
    allergies: "",
    dietaryRestrictions: "",
    likedCuisines: "",
    dislikedCuisines: "",
    days: 3,
  });
  const [mealSettings, setMealSettings] = useState([
    {
      name: "Breakfast",
      caloriePercent: 25,
      proteinPercent: 30,
      fatPercent: 20,
      carbPercent: 50,
      time: "08:00",
      likedIngredients: "",
      dislikedIngredients: "",
    },
    {
      name: "Lunch",
      caloriePercent: 35,
      proteinPercent: 30,
      fatPercent: 25,
      carbPercent: 45,
      time: "12:30",
      likedIngredients: "",
      dislikedIngredients: "",
    },
    {
      name: "Dinner",
      caloriePercent: 40,
      proteinPercent: 35,
      fatPercent: 30,
      carbPercent: 40,
      time: "19:00",
      likedIngredients: "",
      dislikedIngredients: "",
    },
  ]);

  // Chat popup state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [foodLogsLoading, setFoodLogsLoading] = useState(false);

  const toggleChatPopup = () => setIsChatOpen(!isChatOpen);
  const memberId = "6571e3cb9a25c707c1ab32b7";

  const generate_mealplan = async () => {
    setShowSettings(false);
    setGenerateLoading(true);
    fetch(`http://0.0.0.0:8080/mealplan`, {
      method: "POST",
      body: JSON.stringify({
        settings: settings,
        mealSettings: mealSettings,
      }),
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setMealPlan(data["response"]);
        setPlanId(data["response"]["plan_id"]);
      })
      .catch((error) => {
        console.error("Error fetching meal plan info:", error);
      })
      .finally(() => {
        setGenerateLoading(false);
      });
  };

  const saveMealPlan = async () => {
    setGenerateLoading(true);
    fetch(`http://0.0.0.0:8080/mealplan/${planId}`, {
      method: "PUT",
      body: JSON.stringify({
        mealPlan: mealPlan,
      }),
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error("Error fetching meal plan info:", error);
      })
      .finally(() => {
        setGenerateLoading(false);
      });
  };

  const get_mealplan_data = () => {
    setFoodLogsLoading(true);
    fetch(`http://0.0.0.0:8080/mealplan/mealplanInfo/${memberId}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setFoodLogs(data["memberLogs"]);
        setDatabaseMeals(data["databaseLogs"]);
      })
      .catch((error) => {
        console.error("Error fetching meal plan info:", error);
      })
      .finally(() => {
        setFoodLogsLoading(false);
      });
  };

  useEffect(() => {
    get_mealplan_data();
  }, []);
  // Functions for meal settings
  const addMealSetting = () => {
    setMealSettings([
      ...mealSettings,
      {
        name: "",
        caloriePercent: 0,
        proteinPercent: 0,
        fatPercent: 0,
        carbPercent: 0,
        time: "",
        likedIngredients: "",
        dislikedIngredients: "",
      },
    ]);
  };
  const removeMealSetting = (index) => {
    setMealSettings(mealSettings.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const newUserMessage = { role: "user", content: input };
    const updatedChatHistory = [...chatMessages, newUserMessage];
    setChatMessages(updatedChatHistory);
    setMessageLoading(true);

    try {
      const response = await fetch(
        "http://0.0.0.0:8080/mealplan/copilot/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_message: input,
            plan_id: planId,
            chat_history: updatedChatHistory,
            selected_meal_id: selectedMealId,
          }),
        }
      );

      const data = await response.json();

      // let jsonStr = null;
      // const markerCreated = "Meal plan created successfully:";
      // const markerPlanUpdated = "Meal plan updated successfully:";
      // const markerMealUpdated = "Meal updated successfully:";
      if (data.response.functionType === "create_meal_plan") {
        delete data.response.functionType;
        // jsonStr = data.response.split(markerCreated)[1].trim();
        const replyMessage = {
          role: "assistant",
          content: "Meal plan created successfully!",
        };
        setChatMessages((prev) => [...prev, replyMessage]);
        setMealPlan(data["response"]);
        setPlanId(data["response"]["plan_id"]);
      } else if (data.response.functionType === "edit_meal_plan") {
        delete data.response.functionType;
        // jsonStr = data.response.split(markerPlanUpdated)[1].trim();
        const replyMessage = {
          role: "assistant",
          content: "Meal plan updated successfully!",
        };
        setChatMessages((prev) => [...prev, replyMessage]);
        // if (jsonStr) {
        //   const validJsonStr = jsonStr.replace(/'/g, '"');
        //   const parsedPlan = JSON.parse(validJsonStr);
        setMealPlan(data["response"]);
        setPlanId(data["response"]["plan_id"]);
        // }
      } else if (data.response.functionType === "edit_meal") {
        // jsonStr = data.response.split(markerMealUpdated)[1].trim();
        const replyMessage = {
          role: "assistant",
          content: "Meal updated successfully!",
        };
        setChatMessages((prev) => [...prev, replyMessage]);
        // if (jsonStr) {
        //   const validJsonStr = jsonStr.replace(/'/g, '"');
        delete data.response.functionType;
        const updatedMeal = data.response;
        // Update the meal plan state immutably using the new schema (days array)
        setMealPlan((prevMealPlan) => {
          if (!prevMealPlan) return prevMealPlan;
          const newDays = prevMealPlan.days.map((day) => {
            const updatedMeals = day.meals.map((meal) => {
              if (meal.meal_id === updatedMeal.meal_id) {
                return updatedMeal;
              }
              return meal;
            });
            return { ...day, meals: updatedMeals };
          });
          return { ...prevMealPlan, days: newDays };
        });
        // }
      } else {
        const replyMessage = { role: "assistant", content: data.response };
        setChatMessages((prev) => [...prev, replyMessage]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong." },
      ]);
    }
    setInput("");
    setMessageLoading(false);
  };

  const renderMealPlan = () => {
    if (mealPlan && mealPlan.days && mealPlan.days.length > 0) {
      return mealPlan.days.map((dayObj, index) => (
        <div key={index} style={styles.dayBlock}>
          <h3>{dayObj.day}</h3>
          <div style={styles.mealsContainer}>
            {dayObj.meals && dayObj.meals.length > 0 ? (
              dayObj.meals.map((meal) => (
                <MealCard
                  key={meal.meal_id}
                  meal={meal}
                  // Use the meal's mealType property if available, or fall back to its meal name
                  mealType={
                    meal.mealType || meal.meal_name || meal.meal || "Meal"
                  }
                  isSelected={selectedMealId === meal.meal_id}
                  onSelect={(mealId) =>
                    setSelectedMealId((prev) =>
                      prev === mealId ? null : mealId
                    )
                  }
                />
              ))
            ) : (
              <p>No meals for this day.</p>
            )}
          </div>
          {dayObj.dayTotals && (
            <div style={styles.dayTotals}>
              <p>
                Totals: Calories: {dayObj.dayTotals.calories}, Protein:{" "}
                {dayObj.dayTotals.protein}, Fat: {dayObj.dayTotals.fat}, Carbs:{" "}
                {dayObj.dayTotals.carbs}
              </p>
            </div>
          )}
        </div>
      ));
    }
    return <p>No meal plan available. Use the settings to create one.</p>;
  };

  // Toggle selection for Meal List
  const handleToggleMealSelection = (mealId) => {
    setSelectedMealIds((prev) =>
      prev.includes(mealId)
        ? prev.filter((id) => id !== mealId)
        : [...prev, mealId]
    );
  };

  // const filteredMeals = defaultMeals.filter((meal) =>
  //   meal.name.toLowerCase().includes(searchQuery.toLowerCase())
  // );

  return (
    <div style={styles.container}>
      {/* Left Panel: Meal Plan Display (with settings icon and dropdown) */}
      <div style={styles.leftPanel}>
        <div style={styles.mealPlanHeader}>
          <h2>Meal Plan</h2>
          <span>
            {generateLoading ? (
              <Spinner></Spinner>
            ) : (
              <span>
                <button disabled={!planId} onClick={saveMealPlan}>
                  Save Mealplan
                </button>
                <button onClick={generate_mealplan}>Generate Mealplan</button>
                <button
                  style={styles.settingsButton}
                  onClick={() => setShowSettings(!showSettings)}
                >
                  ⚙️
                </button>
              </span>
            )}
          </span>
        </div>
        {showSettings && (
          <SettingsDropdown
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            settings={settings}
            setSettings={setSettings}
            mealSettings={mealSettings}
            setMealSettings={setMealSettings}
            addMealSetting={addMealSetting}
            removeMealSetting={removeMealSetting}
          />
        )}
        {renderMealPlan()}
      </div>

      {/* Right Panel: Two Columns (Left: Meal List; Right: User Info) */}
      <div style={styles.rightPanel}>
        <div style={styles.mealListColumn}>
          <h3>Meal Search</h3>

          {/* Tabs for switching between Food Logs and Database */}
          <div style={styles.tabContainer}>
            <button
              style={activeTab === "Food Logs" ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab("Food Logs")}
            >
              Food Logs
            </button>
            <button
              style={activeTab === "Database" ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab("Database")}
            >
              Database
            </button>
          </div>
          {/* Search Bar */}
          <input
            type="text"
            placeholder="Search meals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchBar}
          />

          {/* Conditionally Render Meals Based on Active Tab */}
          {foodLogsLoading ? (
            <Spinner></Spinner>
          ) : (
            <div style={styles.mealList}>
              {(activeTab === "Food Logs"
                ? filteredFoodLogs
                : filteredDatabaseMeals
              ).map((meal) => (
                <MealListCard
                  key={meal.meal_id}
                  meal={meal}
                  isSelected={selectedMealIds.includes(meal.meal_id)}
                  onToggleSelect={handleToggleMealSelection}
                />
              ))}
            </div>
          )}
        </div>

        <div style={styles.userInfoColumn}>
          <UserInfo memberId={memberId} />
        </div>
      </div>

      {/* Floating Action Button for Chat Popup */}
      <button style={styles.fab} onClick={toggleChatPopup}>
        💬
      </button>
      <ChatPopup
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        chatMessages={chatMessages}
        sendMessage={sendMessage}
        input={input}
        setInput={setInput}
        loading={messageLoading}
      />
    </div>
  );
}

// ─────────────── Inline Styles ───────────────
const styles = {
  container: {
    display: "flex",
    height: "100vh",
    fontFamily: "Arial, sans-serif",
    position: "relative",
  },
  // Left Panel (Meal Plan)
  leftPanel: {
    flex: 1,
    borderRight: "1px solid #ccc",
    padding: "1rem",
    overflowY: "auto",
    position: "relative",
  },
  mealPlanHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  settingsButton: {
    fontSize: "1.5rem",
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  // Settings Dropdown (covers half the height of the left panel)
  settingsDropdown: {
    position: "absolute",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    borderRadius: "5px",
    padding: "1rem",
    width: "850px",
    height: "600px",
    zIndex: 100,
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
  },
  settingsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    marginBottom: "1rem",
  },
  mealSettingsContainer: {
    display: "flex",
    overflowX: "auto",
    gap: "0.5rem",
  },
  mealSettingCard: {
    minWidth: "150px",
    border: "1px solid #ccc",
    borderRadius: "5px",
    padding: "0.5rem",
    backgroundColor: "#f9f9f9",
  },
  // Right Panel (split into two columns)
  rightPanel: {
    flex: 1,
    display: "flex",
    // flexDirection: 'row',
    overflowY: "auto",
  },
  // Left Column of Right Panel: Meal List
  mealListColumn: {
    flex: 1,
    borderRight: "1px solid #ccc",
    display: "flex",
    flexDirection: "column",
    padding: "1rem",
    overflowY: "scroll", // scrollbar can be hidden with additional CSS if needed
  },
  searchBar: {
    padding: "0.5rem",
    marginBottom: "1rem",
    fontSize: "1rem",
  },
  mealList: {
    flex: 1,
    overflowY: "auto",
  },
  // Right Column of Right Panel: User Info
  userInfoColumn: {
    flex: 1,
    padding: "1rem",
    overflowY: "auto",
  },
  // Meal Card styles (for Meal Plan)
  mealCard: {
    flex: "1 1 30%",
    margin: "0.5rem",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: "5px",
    backgroundColor: "#fff",
  },
  checkbox: {
    position: "absolute",
    top: "5px",
    right: "5px",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
  },
  // Meal List Card styles
  mealListCard: {
    position: "relative",
    border: "1px solid #ccc",
    borderRadius: "5px",
    padding: "0.5rem",
    marginBottom: "0.5rem",
    backgroundColor: "#fff",
  },
  mealImage: {
    width: "50px",
    height: "50px",
    borderRadius: "10px",
    objectFit: "cover",
    marginRight: "10px",
  },
  listCheckbox: {
    position: "absolute",
    top: "5px",
    right: "5px",
    width: "20px",
    height: "20px",
  },
  mealHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
  },
  dayBlock: {
    marginBottom: "1rem",
    padding: "1rem",
    border: "1px solid #ddd",
    borderRadius: "5px",
  },
  macroContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "5px",
  },
  macroColumn: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    position: "relative",
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    maxWidth: "90%",
    maxHeight: "90%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  modalImage: {
    maxWidth: "100%",
    maxHeight: "80vh",
    borderRadius: "10px",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    background: "red",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    fontSize: "18px",
    cursor: "pointer",
  },
  // Floating Action Button for Chat Popup
  fab: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    fontSize: "2rem",
    cursor: "pointer",
    zIndex: 1000,
  },
  // Chat Popup (200px x 400px, positioned above the FAB)
  chatPopup: {
    position: "fixed",
    bottom: "90px",
    right: "20px",
    width: "400px",
    height: "600px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    zIndex: 1001,
    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
  },
  chatPopupHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.5rem",
    borderBottom: "1px solid #ccc",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "1.2rem",
    cursor: "pointer",
  },
  chatPopupWindow: {
    flex: 1,
    padding: "0.5rem",
    overflowY: "auto",
  },
  inputArea: {
    display: "flex",
    borderTop: "1px solid #ccc",
  },
  input: {
    flex: 1,
    padding: "0.5rem",
    fontSize: "1rem",
  },
  button: {
    padding: "0.5rem 1rem",
    fontSize: "1rem",
  },
  loader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
  },
  dot: {
    width: "8px",
    height: "8px",
    margin: "0 3px",
    backgroundColor: "#007bff",
    borderRadius: "50%",
    animation: "dotFlashing 1.4s infinite ease-in-out both",
  },
  // User Info styles
  userInfoContainer: {
    padding: "1rem",
  },
  userProfile: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  userBio: {
    margin: 0,
    fontSize: "0.9rem",
    color: "#666",
  },
  profilePicPlaceholder: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    backgroundColor: "#ccc",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  profilePicText: {
    color: "#fff",
  },
  progressSection: {
    display: "flex",
    justifyContent: "space-around",
    marginBottom: "1rem",
  },
  circularProgress: {
    textAlign: "center",
  },
  circularProgressBar: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    border: "5px solid green",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto",
  },
  progressLabel: {
    fontSize: "0.8rem",
  },
  graphSection: {
    marginBottom: "1rem",
  },
  glycemicSection: {
    marginBottom: "1rem",
  },
  pieAndListContainer: {
    display: "flex",
    alignItems: "center",
  },
  highGlycemicList: {
    flex: 1,
    maxHeight: "100px",
    overflowY: "auto",
    border: "1px solid #ccc",
    borderRadius: "5px",
    padding: "0.5rem",
    marginLeft: "1rem",
  },
  glycemicFoodItem: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.2rem",
    fontSize: "0.9rem",
  },
  tabContainer: {
    display: "flex",
    marginBottom: "10px",
  },
  tab: {
    flex: 1,
    padding: "10px",
    cursor: "pointer",
    backgroundColor: "#f1f1f1",
    border: "none",
    textAlign: "center",
  },
  activeTab: {
    flex: 1,
    padding: "10px",
    cursor: "pointer",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    textAlign: "center",
    fontWeight: "bold",
  },
  userMsg: {
    backgroundColor: "#DCF8C6", // Light green for user messages
    color: "#000", // Black text for readability
    padding: "10px",
    borderRadius: "8px",
    maxWidth: "70%",
    alignSelf: "flex-end", // Align user messages to the right
    marginBottom: "8px",
    fontSize: "14px",
    wordBreak: "break-word",
  },
  assistantMsg: {
    backgroundColor: "#EDEDED", // Light gray for assistant messages
    color: "#000", // Black text for readability
    padding: "10px",
    borderRadius: "8px",
    maxWidth: "70%",
    alignSelf: "flex-start", // Align assistant messages to the left
    marginBottom: "8px",
    fontSize: "14px",
    wordBreak: "break-word",
  },
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    padding: "10px",
  },
};

export default MealPlannerCopilot;
